import type { ForwardBuyConstraint } from '@erp/shared-types';
import { gapToThreshold } from '@erp/shared-utils';

/**
 * The threshold a vendor cart has to clear, and how far short of it the cart
 * is, computed live from the cart summary the belt already carries
 * (docs/procurement/forward-buying.md, "Board surfaces").
 *
 * The rule is the one the forward-buy pass targets (`bindingConstraint` in the
 * backend's `vendorCartThresholds.ts`): the higher of the minimum order value
 * and the prepaid-freight minimum binds, and MOV wins a tie because clearing a
 * hard minimum is the stricter claim.
 */

export interface BindingThreshold {
  constraint: ForwardBuyConstraint;
  amount: number;
}

export interface CartShortfall extends BindingThreshold {
  /** Dollars still missing, rounded to the cent; always positive. */
  gap: number;
}

/** Null when the vendor quotes neither threshold for this cart. */
export function bindingThreshold(
  movThreshold: number | null,
  freightThreshold: number | null,
): BindingThreshold | null {
  const mov = movThreshold ?? 0;
  const freight = freightThreshold ?? 0;
  const amount = Math.max(mov, freight);
  if (amount <= 0) return null;
  return { constraint: mov >= freight ? 'mov' : 'freight', amount };
}

/**
 * How far the cart's committed total is below its binding threshold, or null
 * when it has cleared it (or has none). The gap is rounded to the cent first,
 * so float residue in the summed total never reports a cart "$0.00 short".
 */
export function cartShortfall(cart: {
  cartTotal: number;
  movThreshold: number | null;
  freightThreshold: number | null;
}): CartShortfall | null {
  const binding = bindingThreshold(cart.movThreshold, cart.freightThreshold);
  if (binding === null) return null;
  const gap = gapToThreshold(cart.cartTotal, binding.amount);
  if (gap <= 0) return null;
  return { ...binding, gap };
}
